// Non-component exports for DeforumScheduleEditor, split out of the .tsx so the
// component file only exports components (react-refresh/only-export-components).

import type { HyperSteps } from './HyperSpeedSlider';
import type { LoraEntry } from './LoraStack';

export interface DeforumConfig {
  base_model: 'sd15' | 'sdxl';
  checkpoint_filename: string;
  fps: number;
  cadence: number;
  width: number;
  height: number;
  num_inference_steps: number;
  guidance_scale: number;
  /** ComfyUI KSampler sampler_name (e.g. euler_ancestral, dpmpp_sde). */
  sampler: string;
  /** ComfyUI KSampler scheduler (e.g. normal, karras). */
  scheduler: string;
  /** 0 = Normal (no Hyper). 1 | 2 | 4 | 8 | 12 = Hyper step counts. */
  hyper_steps?: HyperSteps;
  /** Active LoRA stack entries (max 3). */
  loras?: LoraEntry[];
}

/**
 * Cadence range + degradation-warning thresholds. Mirrors
 * constants/video_generation/deforum.py (DEFORUM_MIN_CADENCE,
 * DEFORUM_MAX_CADENCE, DEFORUM_CADENCE_HIGH_DEGRADATION_THRESHOLD,
 * DEFORUM_CADENCE_SEVERE_DEGRADATION_THRESHOLD) -- not auto-exported to
 * generated.ts (that module isn't in the auto-export scan), so kept here
 * like DEFORUM_RESOLUTION_BY_BASE_MODEL below. 100 is the pinned
 * deforum-comfy-nodes widget's own ceiling (deforum_ui_data.py:
 * deforum_cadence_params, spinbox min=0 max=100) -- min is clamped to 1 here
 * (not 0) because cadence=0 stalls the DeforumIteratorNode's frame advance.
 */
export const DEFORUM_MIN_CADENCE = 1;
export const DEFORUM_MAX_CADENCE = 100;
export const DEFORUM_CADENCE_HIGH_DEGRADATION_THRESHOLD = 4;
export const DEFORUM_CADENCE_SEVERE_DEGRADATION_THRESHOLD = 10;

/**
 * Curated ComfyUI KSampler sampler_name options exposed in the Sampler tab.
 * Kept short on purpose — these are the samplers that matter for Deforum /
 * fast checkpoints. `dpmpp_sde` is what Lightning checkpoints need.
 */
export const DEFORUM_SAMPLER_OPTIONS: readonly string[] = [
  'euler_ancestral',
  'euler',
  'dpmpp_2m',
  'dpmpp_sde',
  'dpmpp_2m_sde',
  'ddim',
  'uni_pc',
];

/** ComfyUI KSampler scheduler options. `karras` is what Lightning needs. */
export const DEFORUM_SCHEDULER_OPTIONS: readonly string[] = [
  'normal',
  'karras',
  'exponential',
  'sgm_uniform',
  'simple',
];

export const DEFAULT_DEFORUM_CONFIG: DeforumConfig = {
  base_model: 'sd15',
  checkpoint_filename: 'v1-5-pruned-emaonly.safetensors',
  fps: 12,
  // Cadence 1 disables camera motion entirely: the pinned deforum-comfy-nodes
  // pack only applies zoom/translation/rotation warp inside the tween-frame
  // interpolation path, which is skipped when cadence=1. Default to 2 so
  // camera motion actually renders (see DEFORUM_DEFAULT_CADENCE backend-side).
  cadence: 2,
  width: 512,
  height: 512,
  num_inference_steps: 25,
  guidance_scale: 7.5,
  sampler: 'euler_ancestral',
  scheduler: 'normal',
  hyper_steps: 0,
  loras: [],
};

export type DeforumOrientation = 'horizontal' | 'vertical' | 'square';

/**
 * Native-ish render resolution per (base model, orientation). Deforum/SD/SDXL
 * are not square-only — they render any width/height (multiples of 8); square
 * was only ever this app's own hardcoded default, not a model limitation. A
 * mismatched orientation (e.g. a square render for a 9:16 target) gets
 * scale-to-cover + center-cropped downstream, throwing away composition and
 * making the "wrong content" symptom worse.
 *
 * SD 1.5 values (512/912) reuse the exact "SD Square/Horizontal/Vertical"
 * entries already offered in RESOLUTIONS (frontend/src/constants/generated.ts)
 * — same numbers used elsewhere in the app for SD1.5-native output.
 *
 * SDXL values are the standard SDXL multi-aspect bucket resolutions (the ones
 * SDXL was actually fine-tuned on for non-square aspect ratios) — same total
 * pixel budget as the 1024x1024 square bucket, unlike naively scaling the SD1.5
 * numbers up 2x which would overshoot SDXL's native pixel budget by ~80%.
 */
export const DEFORUM_RESOLUTION_BY_BASE_MODEL: Record<
  'sd15' | 'sdxl',
  Record<DeforumOrientation, { width: number; height: number }>
> = {
  sd15: {
    square: { width: 512, height: 512 },
    horizontal: { width: 912, height: 512 },
    vertical: { width: 512, height: 912 },
  },
  sdxl: {
    square: { width: 1024, height: 1024 },
    horizontal: { width: 1344, height: 768 },
    vertical: { width: 768, height: 1344 },
  },
};

/** Derive orientation from a target resolution string like "1920x1080". */
export function deriveOrientation(resolution: string | undefined): DeforumOrientation {
  const match = /^(\d+)x(\d+)$/.exec(resolution ?? '');
  if (!match) return 'horizontal';
  const width = Number(match[1]);
  const height = Number(match[2]);
  if (width === height) return 'square';
  return width > height ? 'horizontal' : 'vertical';
}

/** Resolve the Deforum render resolution for a base model + target orientation. */
export function resolveDeforumResolution(
  baseModel: 'sd15' | 'sdxl',
  orientation: DeforumOrientation
): { width: number; height: number } {
  return DEFORUM_RESOLUTION_BY_BASE_MODEL[baseModel][orientation];
}
